import {
    CollectionDataLoaded,
    ComponentServices,
    createRegisterableComponentWithData,
    DataLayerEventName,
    ListingCollectionDataLoaded,
    useQueryParams,
} from '@news-mono/web-common'
import { DataDefinition } from '@west-australian-newspapers/json-react-layouts-data-loader'
import React from 'react'
import {
    StyledSpinnerContainer,
    StyledIconSpinner,
} from '../SearchResults/SearchResults.styled'
import { SearchResults } from './SearchResults'
import { Paging } from '../../data-controllers/Pagination/usePagination'
import { getSearchEventArgs } from '../../result-filters/filter-events-util'
import { useTheme } from '@emotion/react'
import {
    getDefaultTopicOption,
    getDefaultLocationOption,
} from '../../result-filters/filter-events-util'
import { loadSearchData, SearchDataLoaderConfig } from './load-search-data'

export type { SearchDataLoaderConfig }

export interface SearchRouteProps {
    searchTerm: string
}

const searchDataDefinitionLoader: DataDefinition<
    SearchDataLoaderConfig,
    CollectionDataLoaded,
    ComponentServices
> = {
    // Requires custom cache key so search terms will update when searching on the search page. Search.
    useRuntimeParams: (props) => {
        return {
            searchTerm: JSON.stringify(props.searchTerm),
            timeFilter: props.timeFilter,
            sortBy: props.sortBy,
            topics: props.topic,
            masthead: props.masthead,
            locations: props.location,
        }
    },
    loadData: (props, services) => loadSearchData(props, services),
}

export const SearchRegistration = createRegisterableComponentWithData(
    'search',
    searchDataDefinitionLoader,
    (props: SearchRouteProps, data, services) => {
        const { queryParams } = useQueryParams()
        const setPage = (page: number) => {
            if (data.dataDefinitionArgs?.options.paging) {
                data.dataDefinitionArgs.options.paging.page = page
            }
        }

        if (!data.loaded) {
            const theme = useTheme()
            return theme.kind === 'sevennews' ? (
                <StyledSpinnerContainer>
                    <StyledIconSpinner
                        color={theme.colors.borders.primary}
                        width={65}
                        height={65}
                        shouldAnimate={false}
                    />
                </StyledSpinnerContainer>
            ) : null
        }

        const theme = useTheme()
        const fallbackTopic = getDefaultTopicOption(theme.kind)
        const fallbackLocation = getDefaultLocationOption(theme.kind)

        services.onEvent({
            type: DataLayerEventName.searchResults,
            originator: 'SearchResults',
            payload: {
                ...getSearchEventArgs(
                    props.searchTerm,
                    queryParams,
                    theme.kind,
                    fallbackTopic,
                    fallbackLocation,
                ),
                totalResults:
                    (data.result as ListingCollectionDataLoaded).total ?? 0,
            },
        })

        return (
            <SearchResults
                onEvent={services.onEvent}
                results={data.result}
                paging={data.dataDefinitionArgs?.options.paging as Paging}
                setPage={setPage}
                searchTerm={props.searchTerm}
            />
        )
    },
)
